"use client";

import * as React from "react";

export type ArticlePopoverPlacement = "rail" | "header";

const COMPACT_QUERY = "(max-width: 1023px)";

function subscribeMedia(query: string, onStoreChange: () => void) {
  const mql = window.matchMedia(query);
  mql.addEventListener("change", onStoreChange);
  return () => mql.removeEventListener("change", onStoreChange);
}

function getMediaSnapshot(query: string) {
  return window.matchMedia(query).matches;
}

function useMediaQuery(query: string) {
  return React.useSyncExternalStore(
    (onStoreChange) => subscribeMedia(query, onStoreChange),
    () => getMediaSnapshot(query),
    () => false,
  );
}

export type HeaderPopoverLayoutOptions = {
  /** Fixed width on desktop / rail; compact header uses min(px, viewport). */
  desktopWidth?: number;
  /** Compact header only: width follows content (e.g. reaction strip). */
  compactFitContent?: boolean;
  headerSide?: "right" | "bottom";
  headerAlign?: "start" | "center" | "end";
  railSide?: "right" | "bottom";
  railAlign?: "start" | "center" | "end";
};

export type HeaderPopoverLayout = {
  side: "top" | "right" | "bottom" | "left";
  align: "start" | "center" | "end";
  collisionPadding: number;
  width: number | string | undefined;
  isCompactHeader: boolean;
  fitContent: boolean;
};

/** Content-sized popovers (reaction); capped to viewport on small screens. */
export function articlePopoverFitContentClass() {
  return "bg-white p-0 w-max max-w-[calc(100vw-32px)]";
}

/** Scrollable cap for header-placement popovers on mobile/tablet (avoids viewport clip). */
export function articlePopoverCompactSheetClass() {
  return "max-h-[min(85dvh,calc(100dvh-env(safe-area-inset-bottom,0px)-24px))] overflow-y-auto overscroll-contain";
}

export function useHeaderPopoverLayout(
  placement: ArticlePopoverPlacement,
  options: HeaderPopoverLayoutOptions = {},
): HeaderPopoverLayout {
  const {
    desktopWidth,
    compactFitContent = false,
    headerSide = "right",
    headerAlign = "start",
    railSide = "right",
    railAlign = railSide === "bottom" ? "end" : "start",
  } = options;

  const isCompact = useMediaQuery(COMPACT_QUERY);
  const isCompactHeader = placement === "header" && isCompact;

  if (isCompactHeader) {
    if (compactFitContent) {
    return {
      side: "bottom",
      align: "center",
      collisionPadding: 24,
      width: undefined,
      isCompactHeader: true,
      fitContent: true,
    };
  }
  return {
    side: "bottom",
    align: "center",
    collisionPadding: 24,
      width:
        desktopWidth != null
          ? `min(${desktopWidth}px, calc(100vw - 32px))`
          : undefined,
      isCompactHeader: true,
      fitContent: false,
    };
  }

  if (placement === "header") {
    return {
      side: headerSide,
      align: headerAlign,
      collisionPadding: 8,
      width: desktopWidth,
      isCompactHeader: false,
      fitContent: false,
    };
  }

  return {
    side: railSide,
    align: railAlign,
    collisionPadding: 8,
    width: desktopWidth,
    isCompactHeader: false,
    fitContent: false,
  };
}
